import React, { useState, useMemo } from "react";
import {
  useProduct,
  useProductsByBrand,
  useProductsByCategory,
} from "../../hooks/useProducts";
import { useCart } from "../../hooks/useCart";
import { useBackOrder } from "../../hooks/useBackOrder";
import BackOrderAdjustModal from "../backorder/BackOrderAdjustModal";
import { useAuth } from "../../hooks/useAuth";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import { isBelowOneCase } from "../../utils/quantityHelpers";
import { formatCurrency } from "../../utils/formatters";
import QuantityInput from "../cart/QuantityInput";
import ProductCarousel from "./ProductCarousel";
import LoadingSpinner from "../common/LoadingSpinner";
import ImageComingSoon from "../common/ImageComingSoon";
import PricingTierTable from "../common/PricingTierTable";

const ProductDetailContent = ({ productId, isModal = false, onClose }) => {
  const { isEmployee } = useAuth();
  const { data: product, isLoading, error } = useProduct(productId);

  const {
    updateProductQuantity,
    getItemQuantityInCart,
    isUpdatingProductQuantity,
  } = useCart();

  const { getItemBackOrderQuantity, canUseBackOrder } = useBackOrder();
  const [showBackOrderModal, setShowBackOrderModal] = useState(false);

  // State for quantity inputs
  const [caseQuantity, setCaseQuantity] = useState(0);
  const [unitQuantity, setUnitQuantity] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Create quantity calculator
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.unitsPerCase, product?.optSellByTypeID]);

  // Get current cart quantity and sync with local state
  const currentCartQuantity = useMemo(() => {
    return product ? getItemQuantityInCart(product.id) : 0;
  }, [product?.id, getItemQuantityInCart]);

  // Sync cart quantity with display
  React.useEffect(() => {
    if (calculator && currentCartQuantity !== undefined) {
      const display = calculator.unitsToDisplay(currentCartQuantity);
      setCaseQuantity(display.cases);
      setUnitQuantity(display.units);
    }
  }, [calculator, currentCartQuantity]);

  // Related products queries
  const { data: brandProducts } = useProductsByBrand(product?.brand?.id, {
    enabled: !!product?.brand?.id,
  });

  const { data: categoryProducts } = useProductsByCategory(
    product?.category?.id,
    { enabled: !!product?.category?.id },
  );

  // Handle quantity updates
  const handleQuantityUpdate = async (newCases, newUnits) => {
    if (!calculator || !product) return;

    const newTotal = calculator.displayToUnits(newCases, newUnits);

    try {
      await updateProductQuantity(product.id, newTotal, {
        unitPrice: (product.discountUnitPrice && product.discountUnitPrice > 0) ? product.discountUnitPrice : product.unitPrice,
        singleUnitFee: product.singleUnitFee,
        productSkuVariant: product,
      });
    } catch (error) {
      console.error("Failed to update product quantity:", error);
      // Revert on error
      const currentDisplay = calculator.unitsToDisplay(currentCartQuantity);
      setCaseQuantity(currentDisplay.cases);
      setUnitQuantity(currentDisplay.units);
    }
  };

  const handleCaseChange = (newCases) => {
    setCaseQuantity(newCases);
    handleQuantityUpdate(newCases, unitQuantity);
  };

  const handleUnitChange = (newUnits) => {
    setUnitQuantity(newUnits);
    handleQuantityUpdate(caseQuantity, newUnits);
  };

  // ---- Back order affordance ----
  // Only offer a back order once less than a full case is left to buy (available minus cart).
  const canCreateBackOrder =
    !isEmployee() && product?.allowBackorder && isBelowOneCase(product, currentCartQuantity);

  const currentBackOrderQty = getItemBackOrderQuantity(product?.id);

  // Calculate totals
  const totalUnits = calculator
    ? calculator.displayToUnits(caseQuantity, unitQuantity)
    : 0;
  const priceInfo = calculator ? calculator.calculatePrice(totalUnits) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <LoadingSpinner size="lg" />
        <p className="mt-3 text-muted">Loading product details...</p>
      </div>
    );
  }

  // Error state - Product not found
  if (error?.isProductNotFound || error?.status === 404) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i
            className="bi bi-exclamation-triangle text-warning"
            style={{ fontSize: "3rem" }}
          ></i>
        </div>
        <h3 className="mb-3">Product Not Found</h3>
        <p className="text-muted mb-4">
          This product is no longer available or has been removed from our
          catalog.
        </p>
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-primary"
            onClick={() => (window.location.href = "/products")}
          >
            <i className="bi bi-grid me-2"></i>
            Browse All Products
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => (window.location.href = "/categories")}
          >
            <i className="bi bi-tags me-2"></i>
            Shop by Category
          </button>
        </div>
      </div>
    );
  }

  // Other errors
  if (error) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h4>Error Loading Product</h4>
          <p>{error.message}</p>
          <button
            className="btn btn-outline-danger"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  // Get max quantities for inputs
  const getMaxCases = () =>
    Math.floor(product.unitAvailable / product.unitsPerCase);
  const getMaxUnits = () => {
    const remaining =
      product.unitAvailable - caseQuantity * product.unitsPerCase;
    return Math.min(product.unitsPerCase - 1, remaining);
  };

  return (
    <div className="product-detail">
      <div className="row">
        {/* Left Column - Images */}
        <div className="col-md-6">
          <div className="product-images">
            {/* Main Image */}
            <div className="main-image-container mb-3">
              {product.images?.[selectedImageIndex]?.fullPath || product.imageUrl ? (
                <img
                  src={
                    product.images?.[selectedImageIndex]?.fullPath ||
                    product.imageUrl
                  }
                  alt={product.name}
                  className="img-fluid rounded main-product-image"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <ImageComingSoon
                  className="rounded"
                  style={{ width: "100%", height: "400px" }}
                />
              )}
            </div>

            {/* Thumbnail Row */}
            {product.images && product.images.length > 1 && (
              <div className="thumbnail-row">
                {product.images.map((image, index) => (
                  <img
                    key={image.id}
                    src={image.fullPath}
                    alt={`${product.name} ${index + 1}`}
                    className={`thumbnail me-2 rounded ${index === selectedImageIndex ? "border border-primary border-3" : "border"}`}
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Product Info */}
        <div className="col-md-6">
          <div className="product-info">
            {/* Brand & SKU */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                {product.brand && (
                  <span className="badge bg-primary me-2">
                    {product.brand.name}
                  </span>
                )}
                {product.category && (
                  <span className="badge bg-secondary me-2">
                    {product.category.name}
                  </span>
                )}
              </div>
              {product.legacySKU && (
                <small className="text-muted">SKU: {product.legacySKU}</small>
              )}
            </div>
            {/* Product Name */}
            <h1 className={isModal ? "h3" : "h2"}>{product.name}</h1>
            {/* Flavor */}
            {product.flavor && (
              <p className="text-muted mb-3">{product.flavor}</p>
            )}
            {/* Stock Status */}
            <div className="mb-3">
              {product.isInStock ? (
                <div className="d-flex align-items-center">
                  <span className="badge bg-stash-accent me-2">In Stock</span>
                  <span className="text-muted">
                    {product.unitAvailable} units available
                  </span>
                </div>
              ) : (
                <span className="badge bg-secondary">Coming Soon</span>
              )}
            </div>
            {/* Pricing */}
            <div className="pricing-section mb-4">
              <div className="row">
                {calculator?.canSellByCase && (
                <div className="col-sm-6">
                  <div className="price-card p-3 bg-light rounded">
                    <h5 className="mb-1">{calculator?.caseLabel || 'Case'} Price</h5>
                    <div className="h4 text-stash-accent mb-0">
                      {calculator?.hasDiscountPrice() && (
                        <span className="text-decoration-line-through text-muted me-2 h6">
                          {formatCurrency(product.unitPrice * product.unitsPerCase)}
                        </span>
                      )}
                      {formatCurrency(calculator?.getBaseUnitPrice() * product.unitsPerCase)}
                    </div>
                    <small className="text-muted">
                      {product.unitsPerCase} {calculator?.unitLabelPlural || 'units'} per {calculator?.caseLabel?.toLowerCase() || 'case'}
                    </small>
                    {calculator?.hasDiscountPrice() && (
                      <div className="mt-1">
                        <span className="badge bg-danger">
                          {Math.round((1 - calculator.getBaseUnitPrice() / product.unitPrice) * 100)}% Off
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                )}
                {calculator?.canSellByUnit && (
                  <div className="col-sm-6">
                    <div className="price-card p-3 bg-light rounded">
                      <h5 className="mb-1">{calculator?.unitLabel || 'Unit'} Price</h5>
                      <div className="h4 text-stash-accent mb-0">
                        {calculator?.hasDiscountPrice() && (
                          <span className="text-decoration-line-through text-muted me-2 h6">
                            {formatCurrency(product.unitPrice)}
                          </span>
                        )}
                        {formatCurrency(
                          calculator?.getBaseUnitPrice(),
                        )}
                      </div>
                      {product.singleUnitFee > 0 && (
                        <small className="text-muted">
                          + {formatCurrency(product.singleUnitFee)} {calculator?.unitLabel?.toLowerCase() || 'unit'} fee
                        </small>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Volume Pricing Table */}
              <PricingTierTable
                product={product}
                currentQuantity={totalUnits}
                compact={false}
                showHeader={true}
              />
            </div>
           
            {/* Quantity Selector - hidden for employees in preview mode */}
            {product.isInStock && !isEmployee() && (
              <div className="quantity-section mb-4">
                <h5 className="mb-3">Select Quantity</h5>
                <div className="row">
                  {calculator?.canSellByCase && (
                  <div className="col-6">
                    <label className="form-label">{calculator?.caseLabelPlural || 'Cases'}</label>
                    <QuantityInput
                      value={caseQuantity}
                      onChange={handleCaseChange}
                      quantityType="cases"
                      product={product}
                      currentUnitQuantity={unitQuantity}
                      disabled={isUpdatingProductQuantity}
                      showCartIndicator={false}
                      isUpdating={isUpdatingProductQuantity}
                    />
                  </div>
                  )}
                  {calculator?.canSellByUnit && product.unitsPerCase > 1 && (
                    <div className="col-6">
                      <label className="form-label">Individual {calculator?.unitLabelPlural || 'Units'}</label>
                      <QuantityInput
                        value={unitQuantity}
                        onChange={handleUnitChange}
                        quantityType="units"
                        product={product}
                        currentCaseQuantity={caseQuantity}
                        disabled={isUpdatingProductQuantity}
                        showCartIndicator={false}
                        isUpdating={isUpdatingProductQuantity}
                      />
                    </div>
                  )}
                </div>

                {/* Total Display with savings info */}
                {totalUnits > 0 && priceInfo && (
                  <div className="total-preview mt-3 p-3 bg-primary bg-opacity-10 rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Subtotal:</span>
                      <span>{formatCurrency(priceInfo.totalPrice)}</span>
                    </div>
                    {priceInfo.hasFeesPerUnit && (
                      <div className="d-flex justify-content-between align-items-center mb-2 text-muted">
                        <span>Break-case Fees:</span>
                        <span>+{formatCurrency(priceInfo.fees)}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <span className="fw-bold">Total:</span>
                      <span className="fw-bold text-stash-accent fs-5">{formatCurrency(priceInfo.grandTotal)}</span>
                    </div>
                    {priceInfo.hasSavings && (
                      <div className="mt-2 text-success small">
                        <i className="bi bi-check-circle-fill me-1"></i>
                        You're saving {formatCurrency(priceInfo.totalSavings)} ({priceInfo.savingsPercent}% off)
                        {priceInfo.isMOQPrice && (
                          <span className="badge bg-success ms-2">Volume Pricing</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Back Order — opens the adjustment modal (appears once depleted or a back order exists).
                In the modal view this button lives in the modal footer instead (see ProductDetailModal). */}
            {!isModal && canUseBackOrder && (canCreateBackOrder || currentBackOrderQty > 0) && (
              <div className="quantity-section mb-4 pt-3 border-top">
                <button className="btn btn-outline-dark" onClick={() => setShowBackOrderModal(true)}>
                  <i className="bi bi-clock-history me-2"></i>
                  {currentBackOrderQty > 0 ? "View Back Order" : "Create Back Order"}
                </button>
              </div>
            )}
            {/* Product Description */}
            {product.description && (
              <div className="description-section mb-4">
                <h5>Description</h5>
                <div
                  className="text-muted product-description"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}
            {/* Product Specifications */}
            <div className="specifications-section mb-4">
              <h5>Product Details</h5>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td>
                      <strong>{calculator?.unitLabelPlural || 'Units'} per {calculator?.caseLabel || 'Case'}:</strong>
                    </td>
                    <td>{product.unitsPerCase}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Weight per {calculator?.unitLabel || 'Unit'}:</strong>
                    </td>
                    <td>{product.weight}g</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Sold By:</strong>
                    </td>
                    <td>{calculator?.soldByText || 'Case Only'}</td>
                  </tr>
                  {product.brand?.url && (
                    <tr>
                      <td>
                        <strong>Brand Website:</strong>
                      </td>
                      <td>
                        <a
                          href={product.brand.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          {product.brand.url}
                        </a>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Batch Information */}
            {product.hasBatches && product.batches.length > 0 && (
              <div className="batch-section mb-4">
                <h5>Test Results </h5>
                <div className="batch-list">
                  {product.batches.map((batch) => (
                    <div
                      key={batch.id}
                      className="batch-item border rounded p-3 mb-2"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{batch.batchName}</h6>
                          <small className="text-muted">
                            Created:{" "}
                            {new Date(batch.dateCreated).toLocaleDateString()}
                          </small>
                        </div>
                        {batch.testingFiles.length > 0 && (
                          <div className="testing-files">
                            {batch.testingFiles.map((file) => (
                              <a
                                key={file.id}
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary me-1"
                                title={file.displayName}
                              >
                                <i className="bi bi-file-earmark-pdf me-1"></i>
                                Testing Results
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Products */}
      {!isModal && (
        <div className="related-products-section mt-5">
          {/* Same Brand Products */}
          {product.brand &&
            brandProducts?.products &&
            brandProducts.products.length > 0 && (
              <div className="mb-5">
                <h4 className="mb-3">More from {product.brand.name}</h4>
                <ProductCarousel
                  categoryId={null}
                  brandId={product.brand.id}
                  categoryName={`${product.brand.name} Products`}
                />
              </div>
            )}

          {/* Same Category Products */}
          {product.category &&
            categoryProducts?.products &&
            categoryProducts.products.length > 0 && (
              <div className="mb-5">
                <h4 className="mb-3">More {product.category.name}</h4>
                <ProductCarousel
                  categoryId={product.category.id}
                  brandId={null}
                  categoryName={product.category.name}
                />
              </div>
            )}
        </div>
      )}

      <BackOrderAdjustModal
        show={showBackOrderModal}
        onHide={() => setShowBackOrderModal(false)}
        product={product}
      />
    </div>
  );
};

export default ProductDetailContent;
